import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Textarea,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { collection, collectionGroup, doc, getDoc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase/config";
import { PRODUCT_VERSIONS_COLLECTION, ProductVersion, productVersionLabel } from "../../productVersions/types";
import DropConnectionDialog, { DropConnectionTarget } from "./DropConnectionDialog";
import { stationCanDrop, stationConnectionText, stationIsConnected } from "./stationConnectionStatus";

type StationRecord = {
  id: string;
  stationName?: string;
  motherboardSerial?: string;
  biosUUID?: string;
  fingerprint?: string;
  status?: string;
  posStationId?: string;
  stationId?: string;
  accountHint?: string;
  connectionEnabled?: boolean;
  deviceSecretHash?: string;
  loginPosUserId?: string;
  loginPosUserName?: string;
  lastSeenAt?: { toDate?: () => Date; toMillis?: () => number } | null;
  registeredAt?: { toDate?: () => Date };
  approvedAt?: { toDate?: () => Date };
  stationType?: string;
  billLetter?: string;
  installedVersion?: string;
  productVersionId?: string;
  deviceId?: string;
  certificateDeviceIds?: Record<string, { id?: string; setAt?: number }>;
};

function stationConnectionLabel(item: StationRecord) {
  return stationConnectionText(item);
}

type RequestRecord = StationRecord & {
  requestedAt?: { toDate?: () => Date };
  restaurantId?: string;
  requestedBy?: string;
  posStationId?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  computerName?: string;
  windowsVersion?: string;
  manufacturer?: string;
  model?: string;
  processor?: string;
  memory?: string;
  ipAddress?: string;
  localIp?: string;
  approvalReason?: string;
  workstationLocation?: string;
  stationType?: string;
  billLetter?: string;
  accountHint?: string;
  connectionEnabled?: boolean;
};

function gatheredValue(value?: string) {
  const text = String(value || "").trim();
  return text || "—";
}

function stationTypeLabel(value?: string) {
  const text = String(value || "").trim();
  const labels: Record<string, string> = {
    pos: "POS terminal",
    handheld: "Handheld device",
    customerTablet: "Customer tablet",
    tablet: "Tablet",
    kiosk: "Kiosk",
    kds: "KDS",
  };
  if (!text) return "—";
  return labels[text] || text;
}

function shownStationId(item: { id?: string; posStationId?: string; stationId?: string }, pending = false) {
  if (pending) return "—";
  if (item.id && /^[0-9a-f]{16}$/.test(item.id)) return item.id;
  if (item.stationId && /^[0-9a-f]{16}$/.test(item.stationId)) return item.stationId;
  return item.posStationId || "—";
}

function certificateSlots(item: { certificateDeviceIds?: Record<string, { id?: string; setAt?: number }> }) {
  const map = item.certificateDeviceIds || {};
  return (["DEV", "ESSAI", "PROD"] as const).flatMap((env) => {
    const id = String(map[env]?.id || "").trim();
    if (!id || id === "0000-0000-0000") return [];
    return [{ env, id }];
  });
}

function certificateIdText(item: { certificateDeviceIds?: Record<string, { id?: string; setAt?: number }> }) {
  const slots = certificateSlots(item);
  if (slots.length === 1) return slots[0].id;
  return slots.map((slot) => `${slot.env} ${slot.id}`).join(", ");
}

async function copyWorkstationDeviceId(storeId: string, item: StationRecord) {
  const deviceId = String(item.deviceId || "").trim();
  if (!/^[0-9A-Za-z]{4}-[0-9A-Za-z]{4}-[0-9A-Za-z]{4}$/.test(deviceId) || deviceId === "0000-0000-0000") return item;
  if (certificateSlots(item).length) return item;
  const setAt = Date.now();
  await updateDoc(doc(db, "stores", storeId, "stations", item.id), {
    "certificateDeviceIds.DEV.id": deviceId,
    "certificateDeviceIds.DEV.setAt": setAt,
  });
  return {
    ...item,
    certificateDeviceIds: {
      ...(item.certificateDeviceIds || {}),
      DEV: { id: deviceId, setAt },
    },
  };
}

function deviceIdsDiffer(item: { deviceId?: string; certificateDeviceIds?: Record<string, { id?: string; setAt?: number }> }) {
  const slots = certificateSlots(item);
  const workstation = String(item.deviceId || "").trim();
  if (!slots.length && !workstation) return false;
  if (!slots.length || !workstation) return true;
  return !slots.some((slot) => slot.id.toUpperCase() === workstation.toUpperCase());
}

const statusColor: Record<string, string> = {
  PENDING: "orange",
  APPROVED: "green",
  REJECTED: "red",
  DEACTIVATED: "gray",
  REMOVED: "gray",
};

function whenLabel(value?: { toDate?: () => Date }) {
  const date = value && typeof value.toDate === "function" ? value.toDate() : null;
  if (!date) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function callableMessage(error: unknown) {
  return error instanceof Error ? error.message : "Request failed";
}

type SharedKind = "approved" | "deactivated" | "pending";

type SharedMatch = {
  storeId: string;
  storeName: string;
  kind: SharedKind;
};

function fingerprintKey(value?: string) {
  return String(value || "").trim().toLowerCase();
}

function joinStoreNames(names: string[]) {
  if (names.length <= 1) return names[0] || "";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

function requestShareNotices(matches: SharedMatch[]) {
  const approved = matches.filter((item) => item.kind === "approved").map((item) => item.storeName);
  const deactivated = matches.filter((item) => item.kind === "deactivated").map((item) => item.storeName);
  const pending = matches.filter((item) => item.kind === "pending").map((item) => item.storeName);
  const lines: string[] = [];
  if (approved.length) lines.push(`This station is also registered to ${joinStoreNames(approved)}.`);
  if (deactivated.length) {
    const ending = deactivated.length === 1 ? "That workstation is deactivated." : "Those workstations are deactivated.";
    lines.push(`This station is also registered to ${joinStoreNames(deactivated)}. ${ending}`);
  }
  if (pending.length === 1) lines.push(`This station has a pending registration request from ${pending[0]}.`);
  if (pending.length > 1) lines.push(`This station has pending registration requests from ${joinStoreNames(pending)}.`);
  return lines;
}

function sharedWithLine(matches: SharedMatch[]) {
  const approved = matches.filter((item) => item.kind === "approved").map((item) => item.storeName);
  if (!approved.length) return "";
  return `Registered, shared with ${joinStoreNames(approved)}.`;
}

async function findSharedWorkstations(storeId: string, fingerprints: string[]) {
  const unique = [...new Set(fingerprints.map(fingerprintKey).filter((value) => /^[0-9a-f]{64}$/.test(value)))];
  const grouped = new Map<string, Map<string, SharedKind>>();
  const remember = (fingerprint: string, ownerId: string, kind: SharedKind) => {
    if (!ownerId || ownerId === storeId) return;
    const rank: Record<SharedKind, number> = { approved: 3, deactivated: 2, pending: 1 };
    const byStore = grouped.get(fingerprint) || new Map<string, SharedKind>();
    const current = byStore.get(ownerId);
    if (!current || rank[kind] > rank[current]) byStore.set(ownerId, kind);
    grouped.set(fingerprint, byStore);
  };
  for (let index = 0; index < unique.length; index += 30) {
    const group = unique.slice(index, index + 30);
    const [stationSnap, requestSnap] = await Promise.all([
      getDocs(query(collectionGroup(db, "stations"), where("fingerprint", "in", group))),
      getDocs(query(collection(db, "stationRegistrationRequests"), where("fingerprint", "in", group))),
    ]);
    stationSnap.docs.forEach((item) => {
      if (item.ref.parent.id !== "stations") return;
      const ownerId = item.ref.parent.parent?.id || "";
      const data = item.data() as { fingerprint?: string; status?: string };
      const status = String(data.status || "");
      const kind: SharedKind | "" = status === "APPROVED" ? "approved" : status === "DEACTIVATED" ? "deactivated" : "";
      if (!kind) return;
      remember(fingerprintKey(data.fingerprint), ownerId, kind);
    });
    requestSnap.docs.forEach((item) => {
      const data = item.data() as { fingerprint?: string; status?: string; restaurantId?: string };
      if (String(data.status || "") !== "PENDING") return;
      remember(fingerprintKey(data.fingerprint), String(data.restaurantId || ""), "pending");
    });
  }
  const ownerIds = [...new Set([...grouped.values()].flatMap((byStore) => [...byStore.keys()]))];
  const names = new Map<string, string>();
  await Promise.all(ownerIds.map(async (ownerId) => {
    const snap = await getDoc(doc(db, "stores", ownerId));
    const name = snap.exists() ? String(snap.data().name || "").trim() : "";
    names.set(ownerId, name || ownerId);
  }));
  const result: Record<string, SharedMatch[]> = {};
  grouped.forEach((byStore, fingerprint) => {
    result[fingerprint] = [...byStore.entries()]
      .map(([ownerId, kind]) => ({ storeId: ownerId, storeName: names.get(ownerId) || ownerId, kind }))
      .sort((left, right) => left.storeName.localeCompare(right.storeName));
  });
  return result;
}

function StationVersionCell({ installedVersion, profileId, versions }: { installedVersion: string; profileId: string; versions: ProductVersion[] }) {
  const profile = versions.find((item) => item.id === profileId);
  const installed = installedVersion || "Not reported";
  const mismatch = Boolean(installedVersion) && (!profile || profile.version !== installedVersion);
  return (
    <Box>
      <Text fontSize="sm">{installed}</Text>
      <Text fontSize="sm" color="gray.600">{profile ? productVersionLabel(profile) : "No profile"}</Text>
      {mismatch ? <Badge mt={1}>Mismatch</Badge> : null}
    </Box>
  );
}

const RegisterStationPanel: React.FC<{ storeId: string }> = ({ storeId }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [savingLimit, setSavingLimit] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [maxStations, setMaxStations] = useState("");
  const [savedMaxStations, setSavedMaxStations] = useState<number | null>(null);
  const [activeStationCount, setActiveStationCount] = useState(0);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [stations, setStations] = useState<StationRecord[]>([]);
  const [versions, setVersions] = useState<ProductVersion[]>([]);
  const [storeVersionId, setStoreVersionId] = useState("");
  const [requests, setRequests] = useState<RequestRecord[]>([]);
  const [sharedByFingerprint, setSharedByFingerprint] = useState<Record<string, SharedMatch[]>>({});
  const [detail, setDetail] = useState<RequestRecord | null>(null);
  const [stationDetail, setStationDetail] = useState<RequestRecord | null>(null);
  const [approvalReason, setApprovalReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [stationAction, setStationAction] = useState<{ id: string; action: "deactivate" | "reactivate"; name: string; stationId?: string } | null>(null);
  const [actionReason, setActionReason] = useState("");
  const [actionConfirmation, setActionConfirmation] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [dropTarget, setDropTarget] = useState<DropConnectionTarget | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [storeSnap, stationSnap, requestSnap, loginSnap, versionSnap] = await Promise.all([
        getDoc(doc(db, "stores", storeId)),
        getDocs(collection(db, "stores", storeId, "stations")),
        getDocs(query(collection(db, "stationRegistrationRequests"), where("restaurantId", "==", storeId))),
        getDocs(collection(db, "stores", storeId, "stationLogins")),
        getDocs(collection(db, PRODUCT_VERSIONS_COLLECTION)),
      ]);
      const store = storeSnap.exists() ? storeSnap.data() : {};
      const logins = new Map(loginSnap.docs.map((item) => {
        const data = item.data() as { posUserId?: string; posUserName?: string };
        return [item.id, { posUserId: String(data.posUserId || ""), posUserName: String(data.posUserName || "") }] as const;
      }));
      setMaxStations(typeof store.maxStations === "number" ? String(store.maxStations) : "1");
      setSavedMaxStations(typeof store.maxStations === "number" ? store.maxStations : 1);
      setActiveStationCount(Number(store.activeStationCount || 0));
      setOwnerEmail(String(store.createdBy || ""));
      setStoreVersionId(String(store.productVersionId || ""));
      setVersions(versionSnap.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<ProductVersion, "id">) })));
      const loadedStations = stationSnap.docs.map((item) => {
        const data = item.data() as Omit<StationRecord, "id">;
        const login = logins.get(item.id) || logins.get(String(data.stationId || "")) || logins.get(String(data.posStationId || ""));
        return {
          id: item.id,
          ...data,
          loginPosUserId: login?.posUserId || "",
          loginPosUserName: login?.posUserName || "",
        };
      });
      const withDeviceIds = await Promise.all(loadedStations.map(async (item) => {
        try {
          return await copyWorkstationDeviceId(storeId, item);
        } catch (error) {
          console.warn("Device ID was not copied:", error);
          return item;
        }
      }));
      const loadedRequests = requestSnap.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<RequestRecord, "id">) }));
      setStations(withDeviceIds);
      setRequests(loadedRequests);
      try {
        setSharedByFingerprint(await findSharedWorkstations(storeId, [
          ...withDeviceIds.map((item) => item.fingerprint || ""),
          ...loadedRequests.map((item) => item.fingerprint || ""),
        ]));
      } catch (error) {
        setSharedByFingerprint({});
        toast({ title: "Could not check shared workstations", description: callableMessage(error), status: "error" });
      }
    } catch (error) {
      toast({ title: "Could not load stations", description: callableMessage(error), status: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [storeId]);

  const limitNumber = maxStations === "" ? null : Number(maxStations);
  const atLimit = savedMaxStations !== null && activeStationCount >= savedMaxStations;

  const saveLimit = async () => {
    if (limitNumber === null || !Number.isInteger(limitNumber) || limitNumber < 0) {
      toast({ title: "Enter a whole number, zero or greater", status: "error" });
      return;
    }
    setSavingLimit(true);
    try {
      const call = httpsCallable(functions, "setStoreMaxStations");
      await call({ storeId, maxStations: limitNumber });
      toast({ title: "Station limit saved", status: "success" });
      await load();
    } catch (error) {
      toast({ title: "Could not save the station limit", description: callableMessage(error), status: "error" });
    } finally {
      setSavingLimit(false);
    }
  };

  const openDetail = (item: RequestRecord) => {
    setStationDetail(null);
    setDetail(item);
    setApprovalReason("");
    setConfirmation("");
  };

  const openStationDetail = (item: StationRecord) => {
    const request = requests.find((entry) => entry.fingerprint && entry.fingerprint === item.fingerprint);
    setDetail(null);
    setDeleteOpen(false);
    setDeleteConfirmation("");
    setStationDetail({ ...(request || {}), ...item, id: item.id });
  };

  const review = async (requestId: string, action: "approve" | "reject") => {
    setBusyId(requestId);
    try {
      const call = httpsCallable(functions, "reviewStationRequest");
      await call({
        requestId,
        action,
        approvalReason: approvalReason.trim(),
        confirmation: confirmation.trim(),
      });
      toast({ title: action === "approve" ? "Station approved" : "Request rejected", status: "success" });
      setDetail(null);
      await load();
    } catch (error) {
      toast({ title: "Could not update the request", description: callableMessage(error), status: "error" });
    } finally {
      setBusyId("");
    }
  };

  const openStationAction = (item: StationRecord, action: "deactivate" | "reactivate") => {
    setStationAction({
      id: item.id,
      action,
      name: item.stationName || "Workstation",
      stationId: item.id,
    });
    setActionReason("");
    setActionConfirmation("");
  };

  const updateStation = async () => {
    if (!stationAction) return;
    const expected = stationAction.action === "deactivate" ? "DEACTIVATE" : "ACTIVATE";
    if (!actionReason.trim() || actionConfirmation.trim() !== expected) return;
    setBusyId(stationAction.id);
    try {
      const call = httpsCallable(functions, "updateRegisteredStation");
      await call({
        storeId,
        stationId: stationAction.stationId || "",
        requestId: "",
        action: stationAction.action,
        approvalReason: actionReason.trim(),
        confirmation: actionConfirmation.trim(),
      });
      const nextStatus = stationAction.action === "deactivate" ? "DEACTIVATED" : "APPROVED";
      setStationDetail((current) => (
        current && current.id === stationAction.id ? { ...current, status: nextStatus } : current
      ));
      toast({
        title: stationAction.action === "deactivate" ? "Workstation deactivated" : "Workstation activated",
        status: "success",
      });
      setStationAction(null);
      await load();
    } catch (error) {
      toast({ title: "Could not update the workstation", description: callableMessage(error), status: "error" });
    } finally {
      setBusyId("");
    }
  };

  const deleteWorkstation = async () => {
    if (!stationDetail || stationDetail.status !== "DEACTIVATED") return;
    if (deleteConfirmation.trim() !== "delete") return;
    setBusyId(stationDetail.id);
    try {
      const call = httpsCallable(functions, "updateRegisteredStation");
      await call({
        storeId,
        stationId: stationDetail.id,
        requestId: "",
        action: "delete",
        approvalReason: "",
        confirmation: deleteConfirmation.trim(),
      });
      toast({ title: "Workstation deleted", status: "success" });
      setStationDetail(null);
      setDeleteOpen(false);
      setDeleteConfirmation("");
      await load();
    } catch (error) {
      toast({ title: "Could not delete the workstation", description: callableMessage(error), status: "error" });
    } finally {
      setBusyId("");
    }
  };

  if (loading) return <Spinner />;

  const pending = requests.filter((item) => item.status === "PENDING");
  const shareMatches = (fingerprint?: string) => sharedByFingerprint[fingerprintKey(fingerprint)] || [];
  const detailShareNotices = detail ? requestShareNotices(shareMatches(detail.fingerprint)) : [];
  const stationShareLine = stationDetail?.status === "APPROVED" ? sharedWithLine(shareMatches(stationDetail.fingerprint)) : "";

  return (
    <VStack align="stretch" spacing={6}>
      <HStack bg="white" border="1px solid" borderColor="#d5e4ea" borderRadius="lg" px={4} py={3} spacing={4}>
        <Text fontWeight="600" flexShrink={0}>Station limit</Text>
        <Text fontSize="sm" color="gray.600" flexShrink={0}>
          {activeStationCount} of {savedMaxStations ?? 1} active
        </Text>
        <Text fontSize="sm" color="gray.700" flexShrink={0}>
          Admin account: {ownerEmail || "—"}
        </Text>
        <Input
          type="number"
          min={0}
          size="sm"
          maxW="88px"
          aria-label="Maximum stations"
          value={maxStations}
          onChange={(event) => setMaxStations(event.target.value)}
        />
        <Button size="sm" onClick={saveLimit} isLoading={savingLimit}>Save</Button>
      </HStack>

      <Box
        bg={pending.length ? "orange.50" : "white"}
        border="2px solid"
        borderColor={pending.length ? "orange.400" : "#d5e4ea"}
        borderRadius="lg"
        p={6}
      >
        <Text fontWeight="700" color={pending.length ? "orange.800" : "gray.800"} mb={4}>Pending requests</Text>
        {pending.length === 0 ? (
          <Text fontSize="sm" color="gray.600">No pending registration requests.</Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Station</Th>
                <Th>Station ID</Th>
                <Th>Request sent</Th>
                <Th>Status</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {pending.map((item) => (
                <Tr key={item.id}>
                  <Td fontWeight="600">
                    {item.stationName || "—"}
                    {requestShareNotices(shareMatches(item.fingerprint)).map((line) => (
                      <Text key={line} mt={1} fontSize="sm" fontWeight="600" color="orange.800">{line}</Text>
                    ))}
                  </Td>
                  <Td fontFamily="mono">{shownStationId(item, true)}</Td>
                  <Td>{whenLabel(item.requestedAt)}</Td>
                  <Td><Badge colorScheme="orange" variant="solid">Pending approval</Badge></Td>
                  <Td textAlign="right">
                    <Button size="sm" variant="outline" onClick={() => openDetail(item)}>Detail</Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
        {detail && (
          <Box mt={4} bg="white" border="1px solid" borderColor="orange.200" borderRadius="md" p={4}>
            <HStack justify="space-between" mb={4}>
              <Text fontWeight="700">Request details</Text>
              <Button size="sm" variant="ghost" onClick={() => setDetail(null)}>Close</Button>
            </HStack>
            {detailShareNotices.length > 0 && (
              <Box mb={4} p={3} bg="orange.50" border="1px solid" borderColor="orange.300" borderRadius="md">
                {detailShareNotices.map((line) => (
                  <Text key={line} fontSize="sm" fontWeight="700" color="orange.800">{line}</Text>
                ))}
              </Box>
            )}
            <VStack align="stretch" spacing={2}>
              {[
                ["Workstation document ID", gatheredValue(stations.find((item) => item.fingerprint && item.fingerprint === detail.fingerprint)?.id)],
                ["Station", detail.stationName || "—"],
                ["Station ID", shownStationId(detail, true)],
                ["Request sent", whenLabel(detail.requestedAt)],
                ["Requested by", detail.requestedBy || "—"],
                ["Technical contact", detail.contactName],
                ["Contact email", detail.contactEmail],
                ["Contact phone", detail.contactPhone],
                ["Workstation location", gatheredValue(detail.workstationLocation)],
                ["Station type", stationTypeLabel(detail.stationType)],
                ["Station letter", gatheredValue(detail.billLetter)],
                ["Computer name", gatheredValue(detail.computerName)],
                ["Operating system", gatheredValue(detail.windowsVersion)],
                ["Manufacturer", gatheredValue(detail.manufacturer)],
                ["Model", gatheredValue(detail.model)],
                ["Processor", gatheredValue(detail.processor)],
                ["Memory", gatheredValue(detail.memory)],
                ["Local IP address", gatheredValue(detail.localIp)],
                ["Public IP address", gatheredValue(detail.ipAddress)],
                ["Motherboard serial", gatheredValue(detail.motherboardSerial)],
                ["BIOS UUID", gatheredValue(detail.biosUUID)],
                ["Fingerprint", gatheredValue(detail.fingerprint)],
              ].filter(([, value]) => value).map(([label, value]) => (
                <HStack key={String(label)} align="flex-start">
                  <Text w="180px" flexShrink={0} fontSize="sm" color="gray.600">{label}</Text>
                  <Text fontSize="sm" fontWeight="600" wordBreak="break-all">{value || "—"}</Text>
                </HStack>
              ))}
            </VStack>
            <FormControl isRequired mt={4}>
              <FormLabel>Remarks for the customer</FormLabel>
              <Textarea
                value={approvalReason}
                onChange={(event) => setApprovalReason(event.target.value)}
                placeholder="Instructions or remarks for the customer"
              />
              <Text fontSize="sm" color="gray.600" mt={2}>
                These remarks are included in the email sent when this request is approved or rejected.
              </Text>
            </FormControl>
            <FormControl isRequired mt={3}>
              <FormLabel>Type "APPROVE" to approve</FormLabel>
              <Input
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                autoComplete="off"
              />
            </FormControl>
            {atLimit && (
              <Text fontSize="sm" color="orange.800" mt={3}>
                This store is at its station limit. Raise the limit before approving.
              </Text>
            )}
            <HStack mt={4}>
              <Button
                variant="outline"
                isDisabled={busyId === detail.id || !approvalReason.trim()}
                onClick={() => review(detail.id, "reject")}
              >
                Reject
              </Button>
              <Button
                isDisabled={busyId === detail.id || atLimit || confirmation !== "APPROVE" || !approvalReason.trim()}
                isLoading={busyId === detail.id}
                onClick={() => review(detail.id, "approve")}
              >
                Approve
              </Button>
            </HStack>
          </Box>
        )}
      </Box>

      <Box bg="white" border="1px solid" borderColor="#d5e4ea" borderRadius="lg" p={6}>
        <Text fontWeight="600" mb={4}>Registered stations</Text>
        {stations.filter((item) => item.status !== "PENDING").length === 0 ? (
          <Text fontSize="sm" color="gray.600">No stations have been approved yet.</Text>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Station</Th>
                <Th>Version</Th>
                <Th>Station ID</Th>
                <Th>Device ID at certificate creation</Th>
                <Th>Device ID at the workstation</Th>
                <Th>Account</Th>
                <Th>Station connection</Th>
                <Th>POS user</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {stations.filter((item) => item.status !== "PENDING").map((item) => {
                const shareLine = item.status === "APPROVED" ? sharedWithLine(shareMatches(item.fingerprint)) : "";
                return (
                <Tr key={item.id}>
                  <Td>{item.stationName || "—"}</Td>
                  <Td>
                    <StationVersionCell
                      installedVersion={item.installedVersion || ""}
                      profileId={item.productVersionId || storeVersionId}
                      versions={versions}
                    />
                  </Td>
                  <Td fontFamily="mono">{shownStationId(item)}</Td>
                  <Td fontFamily="mono">
                    {certificateIdText(item) || "—"}
                  </Td>
                  <Td>
                    <Text fontFamily="mono" fontSize="sm">{item.deviceId || "—"}</Text>
                    {deviceIdsDiffer(item) && (
                      <Text mt={1} fontSize="sm" fontWeight="600" color="red.600">Device IDs do not match</Text>
                    )}
                  </Td>
                  <Td fontFamily="mono">{item.accountHint || "—"}</Td>
                  <Td>
                    <Badge bg={stationIsConnected(item) ? "green.500" : "gray.200"} color={stationIsConnected(item) ? "white" : "gray.700"}>{stationConnectionLabel(item)}</Badge>
                  </Td>
                  <Td title={item.loginPosUserId || ""}>
                    {item.loginPosUserId
                      ? `${item.loginPosUserName || "POS user"} ····${item.loginPosUserId.slice(-4)}`
                      : "—"}
                  </Td>
                  <Td>
                    <Badge colorScheme={statusColor[item.status || ""] || "gray"}>{item.status}</Badge>
                    {shareLine && (
                      <Text mt={1} fontSize="sm" fontWeight="600">{shareLine}</Text>
                    )}
                  </Td>
                  <Td>
                    <Button size="xs" variant="outline" onClick={() => openStationDetail(item)}>Detail</Button>
                  </Td>
                </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
        {stationDetail && (
          <Box mt={4} p={4} border="1px solid" borderColor="#d5e4ea" borderRadius="md" bg="gray.50">
            <HStack justify="space-between" align="flex-start" mb={3}>
              <Text fontWeight="600">Workstation record</Text>
              {(stationDetail.status === "APPROVED" || stationDetail.status === "DEACTIVATED") && (
                <Menu>
                  <MenuButton
                    as={IconButton}
                    aria-label="Workstation actions"
                    icon={
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <circle cx="5" cy="12" r="2" />
                        <circle cx="12" cy="12" r="2" />
                        <circle cx="19" cy="12" r="2" />
                      </svg>
                    }
                    variant="ghost"
                    size="sm"
                  />
                  <MenuList>
                    {stationDetail.status === "APPROVED" && (
                      <MenuItem onClick={() => openStationAction(stationDetail, "deactivate")}>Deactivate</MenuItem>
                    )}
                    {stationDetail.status === "DEACTIVATED" && (
                      <MenuItem isDisabled={atLimit} onClick={() => openStationAction(stationDetail, "reactivate")}>Activate</MenuItem>
                    )}
                  </MenuList>
                </Menu>
              )}
            </HStack>
            <VStack align="stretch" spacing={2}>
              {[
                ["Workstation document ID", stationDetail.id || "—"],
                ["Station", stationDetail.stationName || "—"],
                ["Station ID", shownStationId(stationDetail)],
                ["Device ID at certificate creation", certificateIdText(stationDetail) || "—"],
                ["Device ID at the workstation", stationDetail.deviceId || "—"],
                ...(deviceIdsDiffer(stationDetail) ? [["Device IDs", "Do not match"] as [string, string]] : []),
                ["Status", stationDetail.status || "—"],
                ...(stationShareLine ? [["Shared workstation", stationShareLine] as [string, string]] : []),
                ["Account", stationDetail.accountHint || "—"],
                ["Station connection", stationConnectionLabel(stationDetail)],
                ["POS user", stationDetail.loginPosUserId ? `${stationDetail.loginPosUserName || "POS user"} (${stationDetail.loginPosUserId})` : "—"],
                ["Registered", whenLabel(stationDetail.registeredAt)],
                ["Station type", stationTypeLabel(stationDetail.stationType)],
                ["Station letter", gatheredValue(stationDetail.billLetter)],
                ["Requested by", stationDetail.requestedBy || "—"],
                ["Technical contact", stationDetail.contactName],
                ["Contact email", stationDetail.contactEmail],
                ["Contact phone", stationDetail.contactPhone],
                ["Workstation location", gatheredValue(stationDetail.workstationLocation)],
                ["Computer name", gatheredValue(stationDetail.computerName)],
                ["Operating system", gatheredValue(stationDetail.windowsVersion)],
                ["Manufacturer", gatheredValue(stationDetail.manufacturer)],
                ["Model", gatheredValue(stationDetail.model)],
                ["Processor", gatheredValue(stationDetail.processor)],
                ["Memory", gatheredValue(stationDetail.memory)],
                ["Local IP address", gatheredValue(stationDetail.localIp)],
                ["Public IP address", gatheredValue(stationDetail.ipAddress)],
                ["Motherboard serial", gatheredValue(stationDetail.motherboardSerial)],
                ["BIOS UUID", gatheredValue(stationDetail.biosUUID)],
                ["Fingerprint", gatheredValue(stationDetail.fingerprint)],
                ["Remarks", stationDetail.approvalReason],
              ].filter(([, value]) => value).map(([label, value]) => (
                <HStack key={String(label)} align="flex-start">
                  <Text w="180px" flexShrink={0} fontSize="sm" color="gray.600">{label}</Text>
                  <Text fontSize="sm" fontWeight="600" wordBreak="break-all">{value || "—"}</Text>
                </HStack>
              ))}
            </VStack>
            {stationCanDrop(stationDetail) && (
              <Button
                mt={4}
                size="sm"
                variant="ghost"
                color="gray.600"
                fontWeight="normal"
                onClick={() => setDropTarget({ storeId, stationId: stationDetail.id, name: stationDetail.stationName || "This workstation" })}
              >
                Drop connection
              </Button>
            )}
            <Box mt={6} pt={4} borderTop="1px solid" borderColor="#d5e4ea">
              {stationDetail.status === "DEACTIVATED" ? (
                deleteOpen ? (
                  <VStack align="stretch" spacing={3}>
                    <Text fontSize="sm">
                      {stationDetail.stationName || "Workstation"}. This removes the workstation so the store can submit the registration again.
                    </Text>
                    <FormControl isRequired>
                      <FormLabel>Please type the word &quot;delete&quot; to delete this workstation.</FormLabel>
                      <Input
                        value={deleteConfirmation}
                        onChange={(event) => setDeleteConfirmation(event.target.value)}
                        autoComplete="off"
                      />
                    </FormControl>
                    <HStack>
                      <Button variant="outline" onClick={() => { setDeleteOpen(false); setDeleteConfirmation(""); }}>Cancel</Button>
                      <Button
                        colorScheme="red"
                        isDisabled={deleteConfirmation.trim() !== "delete"}
                        isLoading={busyId === stationDetail.id}
                        onClick={deleteWorkstation}
                      >
                        Delete
                      </Button>
                    </HStack>
                  </VStack>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    onClick={() => { setDeleteOpen(true); setDeleteConfirmation(""); }}
                  >
                    Delete workstation
                  </Button>
                )
              ) : (
                <Box p={3} bg="gray.100" borderRadius="md">
                  <Text fontSize="sm" fontWeight="600">Delete unavailable</Text>
                  <Text fontSize="sm" color="gray.700">Deactivate this workstation before it can be deleted.</Text>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
      <DropConnectionDialog target={dropTarget} onClose={() => setDropTarget(null)} onDropped={load} />
      <Modal isOpen={Boolean(stationAction)} onClose={() => setStationAction(null)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {stationAction?.action === "deactivate" ? "Deactivate workstation" : "Activate workstation"}
          </ModalHeader>
          <ModalBody>
            <Text fontSize="sm" mb={4}>
              {stationAction?.name}. Enter remarks for the customer, then type the confirmation word.
            </Text>
            <FormControl isRequired>
              <FormLabel>Remarks for the customer</FormLabel>
              <Textarea
                value={actionReason}
                onChange={(event) => setActionReason(event.target.value)}
                placeholder="Instructions or remarks for the customer"
              />
            </FormControl>
            <FormControl isRequired mt={3}>
              <FormLabel>
                {stationAction?.action === "deactivate" ? 'Type "DEACTIVATE" to deactivate' : 'Type "ACTIVATE" to activate'}
              </FormLabel>
              <Input
                value={actionConfirmation}
                onChange={(event) => setActionConfirmation(event.target.value)}
                autoComplete="off"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={() => setStationAction(null)}>Cancel</Button>
            <Button
              colorScheme={stationAction?.action === "reactivate" ? "blue" : "red"}
              isLoading={busyId === stationAction?.id}
              isDisabled={
                !actionReason.trim()
                || actionConfirmation.trim() !== (stationAction?.action === "deactivate" ? "DEACTIVATE" : "ACTIVATE")
              }
              onClick={updateStation}
            >
              {stationAction?.action === "deactivate" ? "Deactivate" : "Activate"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default RegisterStationPanel;
